import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSelectProps } from 'src/core/POS/PosSelect';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { BuyerInfo, SaleClient } from 'src/WebApiController';

export type UseBuyerSelectorOptions = {
  disabled?: boolean;
  additionalOptions?: Record<string, string>;
  additionalOptionsPosition?: 'top' | 'bottom';
  placeholderText?: string | ContentId;
};

export const useBuyerSelector = ({
  disabled,
  additionalOptions,
  additionalOptionsPosition = 'bottom',
  placeholderText,
}: UseBuyerSelectorOptions): {
  query: UseQueryResult<BuyerInfo[] | null>;
  posSelectProps: Pick<
    PosSelectProps,
    'placeholderText' | 'valueOptionsContent'
  >;
} => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const shouldQuery =
    !disabled && activeAccountWebClientConfig.activeAccountId != null;
  const query = useQuery({
    queryKey: [
      'getAllAvailableBuyers',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return tryInvokeApi(
        () => {
          return new SaleClient(
            activeAccountWebClientConfig
          ).getAllAvailableBuyers();
        },
        (error) => {
          trackError('SaleClient.getAllAvailableBuyers', error);
        }
      );
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });

  const availableBuyers = useMemo(() => {
    const dataSorted = query.data?.toSorted((a: BuyerInfo, b: BuyerInfo) =>
      a.email.localeCompare(b.email)
    );

    const results = dataSorted?.reduce(
      (r, cur) => {
        r[cur.email] = `${cur.email}${cur.name ? ` (${cur.name})` : ''}`;
        return r;
      },
      (additionalOptions && additionalOptionsPosition === 'top'
        ? additionalOptions
        : {}) as Record<string, string>
    );

    return {
      ...(additionalOptionsPosition === 'top' ? additionalOptions : {}),
      ...(results ?? {}),
      ...(additionalOptionsPosition === 'bottom' ? additionalOptions : {}),
    };
  }, [query.data, additionalOptions, additionalOptionsPosition]);

  return {
    query,
    posSelectProps: {
      placeholderText: placeholderText ?? ContentId.ChooseBuyer,
      valueOptionsContent: availableBuyers,
    },
  };
};
